/**
 * TileRenderer — render a frozen TileMap to a PixiJS scene-graph subtree.
 *
 * # What this file owns
 *
 *   1. **Tile-to-sprite mapping** — given an integer TileType at (x, y),
 *      pick the appropriate texture from the LoadedAtlas using a
 *      deterministic spatial hash for variant selection.
 *   2. **Sprite construction** — one PixiJS Sprite per tile, anchored at
 *      bottom-center to align with Phase 1's anchor convention.
 *   3. **Z-ordering** — assign each sprite a `zIndex` derived from its
 *      tile Y coordinate, so tiles and (future) entities co-sort
 *      correctly via `sortableChildren = true` on the world container.
 *   4. **Viewport culling** — each tile sprite is `cullable = true`,
 *      which CullerPlugin (registered in pixiMount.ts) uses to skip
 *      rendering off-screen tiles automatically.
 *   5. **Lifecycle** — `build()` constructs the scene-graph subtree
 *      once; `dispose()` tears it down and detaches from any parent.
 *
 * # What this file does NOT own
 *
 *   - Texture loading (Layer 2 — `atlasLoader.ts`)
 *   - Sprite rendering for entities (Layer 4 — `spriteRenderer.ts`)
 *   - Camera positioning / pan / zoom (Layer 5 — `cameraController.ts`)
 *   - PixiJS Application lifecycle (Layer 1 — `pixiMount.ts`)
 *   - Per-frame updates — tiles don't move, no ticker callback registered
 *
 * # Design decisions (LOCKED Layer 3)
 *
 * ## Decision 1: Tile variant selection via deterministic spatial hash
 *
 *   `TileMap.tiles[i]` holds an integer TileType (Floor, FloorPatch,
 *   Prop, Door, Hazard). The Compact biome ships 12 floor PNGs but
 *   the TileType only says "this is a floor." We pick which floor
 *   (`compact.floor.01` through `compact.floor.12`) via a spatial
 *   hash of `(tileX, tileY)`. Same map seed produces identical
 *   visual output across machines (anti-cheat replay correctness).
 *
 *   Hash: `(tileX * 73856093) ^ (tileY * 19349663)` mod variantCount.
 *   These two prime constants are the standard Teschner et al.
 *   spatial-hash multipliers — well-distributed for 2D integer coords.
 *
 *   Future generators can override the hash by storing an explicit
 *   variant in a future `tileVariant` array (SAVE_VERSION 2 -> 3).
 *   Until then, the hash is the default.
 *
 * ## Decision 2: One Sprite per tile, no Mesh
 *
 *   PixiJS v8's batcher merges sprites sharing textures into very few
 *   GPU draw calls. For a 50x50 arena (2500 sprites) on modern hardware
 *   this is a non-issue. If horde mode (Phase 13) profiling shows it's
 *   worth swapping to a custom Mesh, the TileRenderer's external
 *   interface (build() returning a Container) doesn't change.
 *
 * ## Decision 3: Z-ordering via sortableChildren + zIndex
 *
 *   Tiles get zIndex from tile-Y plus a tiny rank for tile-type
 *   layering within the same row. Layer 4 entities will set their
 *   zIndex from world-Y position, and the same `sortableChildren`
 *   container sorts both. This is the idiomatic PixiJS pattern and
 *   future-proofs every entity-bearing layer (sprites, projectiles,
 *   effects, NPCs, story scripted entities) without rework.
 *
 *   zIndex formula:   zIndex = tileY * Z_SCALE + tileTypeRank
 *   Z_SCALE = 1000.   tileTypeRank: Floor=0, FloorPatch=1, Hazard=2,
 *                                   Prop=3, Door=4.
 *
 *   Floor draws first, FloorPatch overlays it, then Hazard markers,
 *   then Props (rocks/buildings/craters which can occlude entities at
 *   the same Y), then Doors (which generally need to draw last in their
 *   row to overlay walls or props at door locations).
 *
 * ## Decision 4: Viewport culling via CullerPlugin
 *
 *   CullerPlugin is registered at module load in `pixiMount.ts`. Each
 *   tile sprite gets `cullable = true`. PixiJS skips off-screen sprites
 *   automatically before rendering. Zero per-frame TileRenderer cost.
 *
 *   Future-proof for arbitrarily large arenas, mobile devices, and any
 *   future mode (raids, federation strategic UI, story scenes) without
 *   a renderer rewrite.
 *
 * ## Prop sub-category resolution (Phase 2 only)
 *
 *   Compact biome has 6 logical prop categories (rocks, craters,
 *   buildings) but TileType.Prop is a single discriminator. For Phase 2
 *   First Playable Milestone, TileType.Prop tiles uniformly hash across
 *   the union {rocks ∪ craters ∪ buildings}. SAVE_VERSION 2 -> 3
 *   (Phase 8 walkable hub) will introduce sub-category fields.
 *
 * # Test contract
 *
 * The companion test mocks `pixi.js` and verifies:
 *   - Sprite count matches tile count
 *   - Z-index ordering monotonic by tileY
 *   - Spatial hash deterministic across calls
 *   - Cullable flag set on every tile
 *   - Anchor set to (0.5, 1.0) — bottom-center
 *   - Position math uses Phase 1's anchor-conversion functions
 *   - Dispose detaches from parent and destroys all sprites
 *
 * @see {@link ./pixiMount} — CullerPlugin registration, App provides Container API
 * @see {@link ./atlasLoader} — provides the LoadedAtlas this consumes
 * @see {@link ./atlasManifests} — declares tile IDs (compact.floor.01, etc.)
 * @see {@link ../../core/map/tileMap} — anchor-conversion math + TileType enum
 * @see {@link ../../core/combat/fixedPoint} — fpToFloat for fp -> px conversion
 */

import { Container, Sprite, type Texture } from 'pixi.js';
import {
  TileType,
  tileXToWorldFp,
  tileYToWorldFp,
} from '@aevons-wake/core';
import { fpToFloat } from '@aevons-wake/core';
import type { TileMap, BiomeId } from '@aevons-wake/core';
import type { LoadedAtlas } from './atlasLoader';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Multiplier for tileY in zIndex computation. Picked large enough that
 * tile-type-rank offsets within a row never reach the next row's zIndex
 * range. Layer 4 sprites use the same Z_SCALE so tiles and entities
 * co-sort correctly.
 *
 *   zIndex = tileY * Z_SCALE + tileTypeRank
 *   Floor at row 5: 5000
 *   Door at row 5:  5004
 *   Floor at row 6: 6000
 *
 * Layer 4 sprites:
 *   sprite zIndex = round(world_y_logical) * Z_SCALE
 *   where world_y_logical = entity.posY / FIXED_POINT_SCALE / TILE_LOGICAL_HEIGHT
 *
 * tileTypeRank stays in [0, Z_SCALE) so entities at world-y = N can
 * slot between row-N tile types if they fall mid-row.
 */
export const Z_SCALE = 1000;

/**
 * Per-TileType rank within a row's z-index range. Lower rank = drawn
 * earlier (bottom of the visual stack at this row).
 *
 * Floor: 0      drawn first (ground layer)
 * FloorPatch: 1 drawn over floor (decorative overlay)
 * Hazard: 2     drawn over floor patches (damage zone markers)
 * Prop: 3       drawn last in row (rocks/buildings can occlude entities)
 * Door: 4       drawn last (overlays props/buildings at door tiles)
 */
const TILE_TYPE_RANK: Record<number, number> = {
  [TileType.Floor]: 0,
  [TileType.FloorPatch]: 1,
  [TileType.Hazard]: 2,
  [TileType.Prop]: 3,
  [TileType.Door]: 4,
};

/**
 * Spatial-hash multiplier constants from Teschner et al. 2003,
 * "Optimized Spatial Hashing for Collision Detection of Deformable
 * Objects." These two primes give well-distributed 2D integer hashes
 * with low collision rate over typical tile-coord ranges.
 *
 * Used here for deterministic tile-variant selection (which floor PNG
 * out of 12 variants does this tile use?). Pure integer math, no floats,
 * bit-exact identical across machines.
 */
const SPATIAL_HASH_PRIME_X = 73856093;
const SPATIAL_HASH_PRIME_Y = 19349663;

/**
 * Compute a deterministic non-negative integer hash of a 2D tile coord.
 *
 * Result distribution is good enough for variant selection across
 * typical tile-count ranges (1-256 variants). Not cryptographically
 * secure; not intended to be.
 *
 * @returns Non-negative integer in roughly [0, 2^32)
 */
export function spatialHash(tileX: number, tileY: number): number {
  // XOR of two prime-multiplied coords. Use the unsigned-right-shift
  // trick at the end to coerce JS's signed 32-bit XOR result to
  // non-negative.
  return ((tileX * SPATIAL_HASH_PRIME_X) ^ (tileY * SPATIAL_HASH_PRIME_Y)) >>> 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-biome tile-id catalog
//
// For each biome, list the available frame IDs per TileType. The renderer
// hashes tile coords to pick one ID from the appropriate list.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Catalog of available frame IDs per TileType for a given biome.
 *
 * - `floor` — IDs to choose from when TileType.Floor
 * - `floor_patch` — IDs for TileType.FloorPatch
 * - `prop` — IDs for TileType.Prop (uniform random across rocks +
 *   craters + buildings for v1; refined to sub-categories at v3)
 * - `door` — IDs for TileType.Door
 * - `hazard` — IDs for TileType.Hazard (currently empty — hazards
 *   render as colored overlays via HazardZone metadata in a future
 *   layer; the tile itself uses a floor texture)
 */
interface BiomeTileCatalog {
  readonly floor: readonly string[];
  readonly floorPatch: readonly string[];
  readonly prop: readonly string[];
  readonly door: readonly string[];
  readonly hazard: readonly string[];
}

/**
 * Build the Compact biome catalog programmatically. We list frame IDs
 * directly rather than re-reading the manifest because the manifest is
 * order-dependent enumeration; this gives a stable lookup table the
 * renderer can hash against.
 */
function buildCompactBiomeCatalog(): BiomeTileCatalog {
  // 12 floor variants: compact.floor.01 ... compact.floor.12
  const floor: string[] = [];
  for (let i = 1; i <= 12; i++) {
    floor.push(`compact.floor.${String(i).padStart(2, '0')}`);
  }

  // 20 floor-patch variants
  const floorPatch: string[] = [];
  for (let i = 1; i <= 20; i++) {
    floorPatch.push(`compact.floor_patch.${String(i).padStart(2, '0')}`);
  }

  // 19 prop variants: 6 craters + 13 rocks + 7 buildings (per pending.md
  // and verified asset recon). Buildings are also non-passable obstacles
  // and render at the prop z-rank, so they live in this category for v1.
  // SAVE_VERSION 2 -> 3 will sub-categorize.
  const prop: string[] = [];
  for (let i = 1; i <= 6; i++) {
    prop.push(`compact.prop.crater.${String(i).padStart(2, '0')}`);
  }
  for (let i = 1; i <= 13; i++) {
    prop.push(`compact.prop.rock.${String(i).padStart(2, '0')}`);
  }
  for (let i = 1; i <= 7; i++) {
    prop.push(`compact.building.${String(i).padStart(2, '0')}`);
  }

  // 2 door variants
  const door = ['compact.door.01', 'compact.door.02'];

  // Hazards reuse floor textures for now; a later layer adds a colored
  // tint or particle overlay above the tile via HazardZone metadata.
  const hazard: string[] = [...floor];

  return { floor, floorPatch, prop, door, hazard };
}

/**
 * Catalog registry by BiomeId. Phase 2 ships only Compact; the other
 * three biomes (drift, aevon_risen, severed) get catalogs added when
 * their asset packs are sourced and imported.
 */
const BIOME_CATALOGS: Partial<Record<BiomeId, BiomeTileCatalog>> = {
  compact: buildCompactBiomeCatalog(),
};

/**
 * Resolve a tile coord + tile type to a specific frame ID using the
 * biome's catalog and the deterministic spatial hash.
 *
 * @returns Frame ID string (e.g. `'compact.floor.07'`) or `null` if
 *   the biome is not yet registered or the catalog has no variants
 *   for this TileType.
 */
export function resolveTileFrameId(
  biomeId: BiomeId,
  tileType: number,
  tileX: number,
  tileY: number,
): string | null {
  const catalog = BIOME_CATALOGS[biomeId];
  if (!catalog) return null;

  let pool: readonly string[];
  switch (tileType) {
    case TileType.Floor:
      pool = catalog.floor;
      break;
    case TileType.FloorPatch:
      pool = catalog.floorPatch;
      break;
    case TileType.Prop:
      pool = catalog.prop;
      break;
    case TileType.Door:
      pool = catalog.door;
      break;
    case TileType.Hazard:
      pool = catalog.hazard;
      break;
    default:
      return null;
  }

  if (pool.length === 0) return null;

  const index = spatialHash(tileX, tileY) % pool.length;
  return pool[index]!;
}

// ─────────────────────────────────────────────────────────────────────────────
// TileRenderer — the public class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for `TileRenderer`.
 */
export interface TileRendererOptions {
  /**
   * Frozen TileMap from Phase 1's map generators. Read-only; the
   * renderer never mutates it.
   */
  readonly tileMap: TileMap;

  /**
   * Loaded atlas containing every textures the catalog references for
   * this map's biome. Produced by AtlasLoader.load(COMPACT_BIOME_MANIFEST).
   * Missing frame IDs fall back to the magenta placeholder via the
   * AtlasLoader's policy; this renderer just treats undefined as
   * "skip this tile" (logs a console.warn).
   */
  readonly atlas: LoadedAtlas;
}

/**
 * Owner of a TileMap's PixiJS scene-graph subtree.
 *
 * Lifecycle:
 *   1. Construct with options
 *   2. Call `build()` to produce the Container; add it to the world
 *      container via `worldContainer.addChild(tileRenderer.build())`
 *   3. Call `dispose()` on teardown (also detaches from parent)
 *
 * Tiles do not move, so there is no per-frame update method. If a tile
 * needs to be visually updated (rare; e.g. a future hub editor), call
 * `dispose()` and reconstruct the renderer with the new TileMap. We
 * deliberately avoid an `updateTile()` method until a feature actually
 * needs it — keeps the surface minimal.
 */
export class TileRenderer {
  private readonly tileMap: TileMap;
  private readonly atlas: LoadedAtlas;
  private container: Container | null = null;
  private sprites: Sprite[] = [];
  private built = false;
  private disposed = false;

  constructor(options: TileRendererOptions) {
    this.tileMap = options.tileMap;
    this.atlas = options.atlas;
  }

  /**
   * Build the scene-graph subtree. Idempotent — second call returns the
   * already-built container without reconstructing.
   *
   * The returned Container has:
   *   - `sortableChildren = true` (z-ordering active)
   *   - `cullable = true` (CullerPlugin auto-skips when off-screen)
   *
   * Each child Sprite has:
   *   - `anchor.set(0.5, 1.0)` (bottom-center, matching Phase 1)
   *   - `position` set via `tileXToWorldFp` / `tileYToWorldFp` + `fpToFloat`
   *   - `zIndex` = tileY * Z_SCALE + tileTypeRank
   *   - `cullable = true`
   *
   * @throws Error if called after dispose()
   */
  build(): Container {
    if (this.disposed) {
      throw new Error(
        'TileRenderer.build: instance has been disposed; construct a new one',
      );
    }
    if (this.built) {
      return this.container!;
    }

    const container = new Container();
    container.label = `tile-renderer-${this.tileMap.biomeId}`;
    container.sortableChildren = true;
    container.cullable = true;

    // Walk the tile grid in row-major order. This is also a reasonable
    // initial child order for the sortableChildren sort: PixiJS's sort
    // is stable, so within-equal-zIndex ties preserve insertion order
    // which gives a consistent left-to-right within a row.
    for (let tileY = 0; tileY < this.tileMap.height; tileY++) {
      for (let tileX = 0; tileX < this.tileMap.width; tileX++) {
        const tileType = this.tileMap.tiles[tileY * this.tileMap.width + tileX]!;
        const sprite = this.makeSpriteForTile(tileX, tileY, tileType);
        if (sprite !== null) {
          container.addChild(sprite);
          this.sprites.push(sprite);
        }
      }
    }

    this.container = container;
    this.built = true;
    return container;
  }

  /**
   * Tear down the scene-graph subtree. Detaches from any parent
   * container if attached, destroys every sprite (without destroying
   * their textures — those are owned by the AtlasLoader). Idempotent.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.container !== null) {
      // Detach from parent if attached. PixiJS handles graceful
      // detachment via removeFromParent.
      if (this.container.parent !== null) {
        this.container.parent.removeChild(this.container);
      }

      // Destroy every sprite. We pass `{ children: false }` because
      // we already enumerated children in `this.sprites` and want to
      // be explicit. We do NOT destroy textures — the AtlasLoader
      // owns texture lifecycle.
      for (const sprite of this.sprites) {
        try {
          sprite.destroy({ children: false, texture: false, textureSource: false });
        } catch (err) {
          console.warn('[TileRenderer] sprite.destroy threw:', err);
        }
      }
      this.sprites = [];

      // Destroy the container itself (without destroying remaining
      // children, since we already destroyed them).
      try {
        this.container.destroy({ children: false });
      } catch (err) {
        console.warn('[TileRenderer] container.destroy threw:', err);
      }
      this.container = null;
    }

    this.built = false;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Test introspection (do not use in production code)
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Test-only: returns the array of sprites created by build(). Empty
   * before build() and after dispose().
   */
  _getSpritesForTests(): readonly Sprite[] {
    return this.sprites;
  }

  /**
   * Test-only: returns the active container, or null before build /
   * after dispose.
   */
  _getContainerForTests(): Container | null {
    return this.container;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Construct a single tile sprite, or return null if the tile cannot
   * be rendered (unknown biome, unknown tile type, missing texture).
   */
  private makeSpriteForTile(
    tileX: number,
    tileY: number,
    tileType: number,
  ): Sprite | null {
    const frameId = resolveTileFrameId(
      this.tileMap.biomeId,
      tileType,
      tileX,
      tileY,
    );
    if (frameId === null) {
      // Unknown biome or unknown TileType. Phase 2 only registers
      // 'compact'; other biomes will register catalogs as their assets
      // arrive. Until then, the tile is silently skipped (no error
      // spam — entire biomes returning null would flood the console).
      return null;
    }

    const texture = this.atlas.get(frameId);
    if (texture === undefined) {
      // Frame ID not present in the atlas. The AtlasLoader's magenta-
      // placeholder policy normally substitutes for missing files, so
      // this branch only fires if the atlas was loaded against a
      // different manifest. Log once per tile (acceptable; reaching
      // this branch is a programming error, not a runtime expectation).
      console.warn(
        `[TileRenderer] no texture for frame "${frameId}" at tile (${tileX}, ${tileY})`,
      );
      return null;
    }

    const sprite = new Sprite(texture as Texture);

    // Bottom-center anchor matches Phase 1's anchor convention. The
    // anchor-conversion functions return the world-space coord of the
    // bottom-center of the visible tile diamond; setting the sprite
    // anchor to (0.5, 1.0) means sprite.position == that world coord.
    sprite.anchor.set(0.5, 1.0);

    // Position from Phase 1's anchor-conversion math. Result is fp;
    // convert to float at the renderer boundary (rule #16).
    const worldXFp = tileXToWorldFp(tileX);
    const worldYFp = tileYToWorldFp(tileY);
    sprite.x = fpToFloat(worldXFp);
    sprite.y = fpToFloat(worldYFp);

    // Z-order: tile-Y dominates so back-of-row draws first; rank
    // disambiguates within a row.
    const rank = TILE_TYPE_RANK[tileType] ?? 0;
    sprite.zIndex = tileY * Z_SCALE + rank;

    // Enable culling. CullerPlugin (registered in pixiMount.ts) will
    // auto-skip this sprite when its bounds fall outside the renderer's
    // screen rectangle.
    sprite.cullable = true;

    // Stable label for DevTools inspection. PixiJS v8 exposes this via
    // the scene-graph debugger.
    sprite.label = `tile-${tileX}-${tileY}-type${tileType}`;

    return sprite;
  }
}
